import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { homeFor } from "../lib/home";
import type { CurrentUser } from "../lib/types";
import { Button, ErrorNote, Field, Input } from "../components/ui";
import { AuthFrame, CompactWordmark } from "../components/Brand";

const MIN_PASSWORD = 12;

/**
 * Opening an account, with nothing bought yet.
 *
 * The same door as signing in, because it is the same door — this is what the
 * "Create one" underneath the password field leads to.
 */
export function Register() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (loading) return <Splash />;
  if (user !== null) return <Navigate to={homeFor(user.role)} replace />;

  // Checked here as well as on the server, because typing a password twice and
  // being told at the end that they differ is a bad way to find out.
  const mismatch = confirmation.length > 0 && confirmation !== password;
  const ready = password.length >= MIN_PASSWORD && confirmation === password;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;

    setError(null);
    setPending(true);

    try {
      const result = await api<{ user: CurrentUser }>("/api/v1/auth/register", {
        method: "POST",
        body: {
          name: name.trim(),
          email: email.trim(),
          company: company.trim() || undefined,
          password,
        },
      });

      // Registering signs them in, so this lands in the portal rather than at
      // a login screen asking for what they chose ten seconds ago.
      setUser(result.user);
      navigate(homeFor(result.user.role), { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Something went wrong. Try again.");
      setPending(false);
    }
  }

  return (
    <AuthFrame>
      <header className="space-y-2">
        <CompactWordmark />
        <h1 className="display pt-4 text-[2.5rem] text-ink lg:pt-0">Create an account</h1>
        <p className="text-sm text-muted">
          Free, and takes a minute. You can buy a plan once you are in.
        </p>
      </header>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <Field label="Your name">
          <Input
            value={name}
            autoComplete="name"
            required
            autoFocus
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <Field label="Company" hint="Optional.">
          <Input
            value={company}
            autoComplete="organization"
            maxLength={200}
            onChange={(event) => setCompany(event.target.value)}
          />
        </Field>

        <Field label="Email">
          <Input
            type="email"
            value={email}
            autoComplete="username"
            required
            maxLength={254}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field label="Choose a password" hint={`At least ${MIN_PASSWORD} characters.`}>
          <Input
            type="password"
            value={password}
            autoComplete="new-password"
            required
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <Field label="Type it again">
          <Input
            type="password"
            value={confirmation}
            autoComplete="new-password"
            required
            onChange={(event) => setConfirmation(event.target.value)}
          />
        </Field>

        {mismatch && <ErrorNote>Those two do not match.</ErrorNote>}
        {error !== null && <ErrorNote>{error}</ErrorNote>}

        <Button
          type="submit"
          variant="primary"
          className="min-h-[2.75rem] w-full"
          disabled={!ready || pending}
        >
          {pending ? "Creating…" : "Create account"}
        </Button>
      </form>

      <p className="mt-8 border-t border-hairline pt-6 text-sm text-dim">
        Already have one?{" "}
        <Link to="/login" className="text-muted underline underline-offset-4 hover:text-ink">
          Sign in
        </Link>
        .
      </p>
    </AuthFrame>
  );
}

function Splash() {
  return (
    <div className="flex min-h-dvh items-center justify-center text-sm text-dim">Loading…</div>
  );
}
